/**
 * Pure geometry helpers for BraidLens.
 * Ported from the v6 reference mock
 * (docs/superpowers/specs/assets/2026-07-05-braid-mock-v6.html) — the SVG
 * constants and the quadratic-midpoint envelope smoothing come from there.
 */

export const VIEW_W = 880;
export const LPAD = 236; // left padding for row headers
export const RPAD = 44;
export const TICK_TOP = 44;
export const FIRST_LANE_Y = 88;
export const LANE_SPACING = 60;
export const LANE_BOTTOM_PAD = 45; // svg height = last lane Y + this
export const TICK_BOTTOM_PAD = 15; // ticks end at last lane Y + this
export const ENVELOPE_PEAK = 44; // px height of the global max cell count
export const ENVELOPE_BASELINE_OFFSET = 17; // baseline = laneY + this
export const ENVELOPE_LEAD = 22; // envelope lead-in/out beyond first/last x
export const DOT_Y_JITTER = 17;
export const MIN_SESSION_COL = 46; // px per session column before h-scroll kicks in (dates are de-cluttered, so columns can pack tighter and still fit ~13 sessions in a 960px window)
export const MIN_LABEL_GAP = 44; // min horizontal gap (viewBox units) between two visible axis date labels before one is dropped
export const DOT_R = 4.3;
export const DOT_R_LATEST = 5.2;
export const HALO_R = 8.7;
export const ECHO_RING_EXTRA = 5.5; // echo ring r = dot r + this

/** Deterministic PRNG (mulberry32). */
export function mulberry32(a: number): () => number {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic per-moment jitter, seeded by the moment id.
 * x amplitude grows with the cell's dot count (capped), y is fixed ±17.
 */
export function momentJitter(
  momentId: number,
  cellCount: number,
): { jx: number; jy: number } {
  const rng = mulberry32(momentId);
  const xAmp = Math.min(26, 8 + 5 * cellCount);
  return {
    jx: (rng() * 2 - 1) * xAmp,
    jy: (rng() * 2 - 1) * DOT_Y_JITTER,
  };
}

/**
 * Session x positions from real dates: linear map of Date.parse between the
 * first and last session onto [LPAD, VIEW_W - RPAD].
 */
export function sessionXPositions(dates: string[]): number[] {
  if (dates.length === 0) {
    throw new Error("[braidGeometry] no sessions to lay out");
  }
  const ts = dates.map((d) => {
    const t = Date.parse(d);
    if (Number.isNaN(t)) {
      throw new Error(`[braidGeometry] unparseable session date: ${d}`);
    }
    return t;
  });
  const first = ts[0];
  const last = ts[ts.length - 1];
  const x0 = LPAD;
  const x1 = VIEW_W - RPAD;
  if (last === first) {
    // A status:"ok" story map always spans multiple sessions (min_sessions
    // gate). A zero span means broken data — fail loudly.
    throw new Error("[braidGeometry] session dates span zero time");
  }
  return ts.map((t) => x0 + ((t - first) / (last - first)) * (x1 - x0));
}

/**
 * Quadratic-midpoint smoothed closed envelope over per-session counts.
 * Peak scaled to ENVELOPE_PEAK px against the global max cell count.
 */
export function envelopePath(
  xs: number[],
  counts: number[],
  baseline: number,
  globalMax: number,
): string {
  if (xs.length !== counts.length) {
    throw new Error(
      `[braidGeometry] xs/counts length mismatch: ${xs.length} vs ${counts.length}`,
    );
  }
  if (xs.length === 0) {
    throw new Error("[braidGeometry] envelopePath needs at least one point");
  }
  if (globalMax <= 0) {
    throw new Error("[braidGeometry] envelopePath needs a positive globalMax");
  }
  const pts: Array<{ x: number; y: number }> = [
    { x: xs[0] - ENVELOPE_LEAD, y: baseline },
    ...xs.map((x, i) => ({
      x,
      y: baseline - (counts[i] / globalMax) * ENVELOPE_PEAK,
    })),
    { x: xs[xs.length - 1] + ENVELOPE_LEAD, y: baseline },
  ];
  const f = (n: number) => Number(n.toFixed(1));
  let d = `M ${f(pts[0].x)} ${f(pts[0].y)}`;
  d += ` Q ${f(pts[0].x)} ${f(pts[0].y)} ${f((pts[0].x + pts[1].x) / 2)} ${f(
    (pts[0].y + pts[1].y) / 2,
  )}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    d += ` Q ${f(pts[i].x)} ${f(pts[i].y)} ${f(mx)} ${f(my)}`;
  }
  d += ` L ${f(pts[pts.length - 1].x)} ${f(pts[pts.length - 1].y)} Z`;
  return d;
}

/** Localized short date for the axis / drawer ("Jul 3" / "júl. 3."). */
export function formatShortDate(dateStr: string, language: string): string {
  const t = Date.parse(dateStr);
  if (Number.isNaN(t)) {
    throw new Error(`[braidGeometry] unparseable date: ${dateStr}`);
  }
  return new Date(dateStr).toLocaleDateString(
    language === "hu" ? "hu-HU" : "en-US",
    { month: "short", day: "numeric" },
  );
}
